import { ref, readonly, type DeepReadonly, type Ref } from 'vue';
import type { ToastInput, ToastItem } from '@/types/types';

export type { ToastTone, ToastAction, ToastInput, ToastItem } from '@/types/types';

const DEFAULT_DURATION_MS = 5000;
const DANGER_DURATION_MS = 8000;

const items = ref<ToastItem[]>([]);
const timers = new Map<string, ReturnType<typeof setTimeout>>();

function scheduleDismiss(toast: ToastItem): void {
  if (toast.duration <= 0) return;
  const timer = setTimeout(() => dismiss(toast.id), toast.duration);
  timers.set(toast.id, timer);
}

function cancelTimer(id: string): void {
  const timer = timers.get(id);
  if (timer !== undefined) {
    clearTimeout(timer);
    timers.delete(id);
  }
}

function generateId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `toast-${crypto.randomUUID()}`;
  }
  return `toast-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function push(input: ToastInput): string {
  const tone = input.tone ?? 'info';
  const duration =
    typeof input.duration === 'number'
      ? input.duration
      : tone === 'danger'
        ? DANGER_DURATION_MS
        : DEFAULT_DURATION_MS;
  const toast: ToastItem = {
    id: generateId(),
    tone,
    title: input.title,
    message: input.message,
    duration,
    action: input.action,
    createdAt: Date.now(),
  };
  items.value = [...items.value, toast];
  scheduleDismiss(toast);
  return toast.id;
}

function dismiss(id: string): void {
  cancelTimer(id);
  items.value = items.value.filter((item) => item.id !== id);
}

function clear(): void {
  for (const id of Array.from(timers.keys())) cancelTimer(id);
  items.value = [];
}

function pause(id: string): void {
  cancelTimer(id);
}

function resume(id: string): void {
  const toast = items.value.find((item) => item.id === id);
  if (!toast) return;
  if (timers.has(id)) return;
  scheduleDismiss(toast);
}

export interface UseToastReturn {
  readonly toasts: DeepReadonly<Ref<readonly ToastItem[]>>;
  readonly push: (input: ToastInput) => string;
  readonly dismiss: (id: string) => void;
  readonly clear: () => void;
  readonly pause: (id: string) => void;
  readonly resume: (id: string) => void;
}

/**
 * Глобальный стор toast-сообщений. Инстанция — singleton на уровне
 * модуля, поэтому несколько компонентов (UI + бизнес-код) видят одну
 * и ту же очередь.
 */
export function useToast(): UseToastReturn {
  return {
    toasts: readonly(items),
    push,
    dismiss,
    clear,
    pause,
    resume,
  };
}
